import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Lock } from 'lucide-react';
import { Input } from '@ooh/ui/components/input';
import { Button } from '@ooh/ui/components/button';
import type { Role } from '@ooh/contracts';
import { IMMUTABLE_ROLE_CODES } from './role-codes';

export interface RoleSidebarGroup {
  id: string;
  /** Translated group label (e.g. "Administratorzy systemu"). */
  label: string;
  roleCodes: string[];
}

export interface RoleSidebarProps {
  roles: Role[];
  /** Already search-filtered groups — filtering stays in the caller (owns the query state). */
  groups: RoleSidebarGroup[];
  selectedRoleCode: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSelectRole: (code: string) => void;
}

/** Left-hand role list: search box + role groups, grouped with visible spacing between sections. */
export function RoleSidebar({
  roles,
  groups,
  selectedRoleCode,
  searchQuery,
  onSearchChange,
  onSelectRole,
}: RoleSidebarProps) {
  const { t } = useTranslation('admin');

  return (
    <div className="w-72 border-r border-border/50 flex flex-col shrink-0 min-h-0">
      <div className="p-3 border-b border-border/40 shrink-0">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('permissionMatrix.sidebar.searchPlaceholder')}
            className="pl-8 h-8 text-[13px]"
            aria-label={t('permissionMatrix.sidebar.searchAriaLabel')}
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {groups.length === 0 ? (
          <p className="px-4 py-6 text-[13px] text-muted-foreground text-center">
            {t('permissionMatrix.sidebar.emptyState')}
          </p>
        ) : (
          groups.map((group, groupIndex) => (
            <div
              key={group.id}
              className={groupIndex === 0 ? '' : 'mt-6 pt-4 border-t border-border/40'}
            >
              <p className="px-4 pb-1.5 text-2xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                {group.label}
              </p>
              {group.roleCodes.map((code) => {
                const role = roles.find((r) => r.code === code);
                if (!role) return null;
                const isSelected = role.code === selectedRoleCode;
                return (
                  <Button
                    key={role.id}
                    variant="ghost"
                    size="sm"
                    fullWidth
                    onClick={() => onSelectRole(role.code)}
                    className={`text-[13px] ${
                      isSelected ? 'bg-muted text-foreground font-medium' : 'text-foreground/80'
                    } ${!role.isActive ? 'opacity-50' : ''}`}
                    data-testid={`role-item-${role.code}`}
                  >
                    <span className="w-full flex items-center gap-2">
                      {IMMUTABLE_ROLE_CODES.includes(role.code) ? (
                        <Lock className="w-3 h-3 shrink-0 text-primary" />
                      ) : null}
                      <span className="flex-1 truncate text-left">{role.name}</span>
                    </span>
                  </Button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
